import React, { useState } from "react";
import { useTranslation } from "utilities/i18n";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import {
  HardwareModel,
  Maybe,
  useHardwareModelsQuery
} from "generated/graphql";
import { Link } from "polyrouter";
import { getDeviceModel } from "routes/Manage/Inventory/Settings/ProvisionedDeviceSettingsRouter";
import { DropdownList, OptionType, PolyIcon } from "elements";
import { protect } from "TsRoleGuards";
import "./AddPolicyModal.scss";
import { matchesCatalogModel } from "utilities/modelSelectors";
import { useRouteMatch } from "react-router";
import { Scope } from "./PolicyList";
import { usePolicyFilterValue } from "elements/Filter/PolicyFilter";
import { PolicyTypes } from "../Inventory/Settings-Desktop/ProvisionedDesktopDeviceSettings";

const AddPolicyModal: React.FC<{
  existingPolicies: { model: string; uuid: Maybe<string> }[];
}> = ({ existingPolicies }) => {
  const { t } = useTranslation();
  const { params, path } = useRouteMatch();
  const [filterState] = usePolicyFilterValue();
  const dispatchEvent = useModalDispatch();
  const [model, setModel] = useState("");

  const { data } = useHardwareModelsQuery();

  let scopedParams = { scope: "" };
  if (path.includes(":scope")) {
    scopedParams = params as Scope;
  }

  let catalogModels: HardwareModel[] = [];
  if (data) {
    catalogModels = (data.hardwareModels as HardwareModel[])
      .filter(model => model.supportsProvisioning || model.supportsPolicies) // allow policies only for models that support provisioning or support policies
      .map(model => {
        const policyFormatModel = getDeviceModel(model.name);
        return {
          ...model,
          name: policyFormatModel
        };
      });
  }

  let linkSearch = `previous=model`;
  if (scopedParams.scope === PolicyTypes.SITE) {
    linkSearch = `site=${filterState.site}&previous=site`;
  }
  if (scopedParams.scope === PolicyTypes.GROUP) {
    linkSearch = `group=${filterState.group}&previous=group`;
  }
  if (scopedParams.scope === PolicyTypes.USER_GROUP) {
    linkSearch = `user_group=${filterState.user_group}&previous=user_group`;
  }

  const disabledOption = (model: HardwareModel) => {
    let disabled = false;
    disabled = existingPolicies.some(
      existingPolicy =>
        matchesCatalogModel(existingPolicy.uuid ?? undefined, model) ||
        matchesCatalogModel(existingPolicy.model, model)
    );
    // Disable for non-desktop devices when scope = USER_GROUP
    if (
      !model.supportsPolicies &&
      scopedParams.scope === PolicyTypes.USER_GROUP
    ) {
      disabled = true;
    }
    return disabled;
  };

  const models: OptionType[] = catalogModels
    .map(model => ({
      text: model.name,
      value: model.name,
      disabled: disabledOption(model)
    }))
    .sort((a, b) => (b.text > a.text ? -1 : 1));

  return (
    <DefaultModalForm
      title={t("ADD_POLICY")}
      onSubmit={() => {}}
      name="AddPolicy"
      className="add-policy-modal"
    >
      {() => (
        <>
          <span className="small-text">{t("ADD_POLICY_NOTE")}</span>
          <div className="form-input mt-2">
            <DropdownList
              name="models"
              value={model}
              onChange={setModel}
              options={models}
              placeholder={t("SEARCH_BY_NAME")}
              search
              inline
            />
            <PolyIcon icon="search" />
          </div>
          {model && (
            <div className="unchanged-policy-note">
              {t("CHANGE_POLICY_TO_SAVE_NOTE_ON_MODAL")}
            </div>
          )}
          <div className="btn-container">
            <button type="button" className="btn" onClick={dispatchEvent}>
              {t("CANCEL")}
            </button>
            <Link
              persist={["PolicyFilterState", "previous"]}
              className={`btn btn-primary ${!model ? "disabled" : ""}`}
              to={{
                pathname: "/manage/policy/details/settings",
                search: `deviceModel=${model}&${linkSearch}`
              }}
            >
              {t("CONTINUE")}
            </Link>
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};

export default protect("update:device")(AddPolicyModal);
